import { AxiosError } from "axios"
import {
  STATUS_MESSAGES,
  DEFAULT_ERROR_MESSAGE,
} from "@/services/ErrorService/statusMessages"

export interface IErrorField {
  name?: string
  errorCategory?: string
  errorCode?: string
}

export type IErrorCause = IErrorField

export interface IErrorResponseBody {
  message?: string
  sessionId?: string
  traceId?: string
  errorCategory?: string
  errorCode?: string
  fields?: IErrorField[]
  causes?: IErrorCause[]
}

export interface IErrorPayload {
  status?: number
  responseBody?: IErrorResponseBody
  // Several endpoints answer with a flat body instead of the gateway envelope.
  message?: string
  error?: string
}

const asPayload = (data: unknown): IErrorPayload =>
  data !== null && typeof data === "object" ? (data as IErrorPayload) : {}

// A flat body repeats the status reason in `error`, so it is only worth
// showing when it says something the status itself does not.
const HTTP_REASON_PHRASES = new Set([
  "Bad Request",
  "Unauthorized",
  "Forbidden",
  "Not Found",
  "Method Not Allowed",
  "Conflict",
  "Unprocessable Entity",
  "Too Many Requests",
  "Internal Server Error",
  "Bad Gateway",
  "Service Unavailable",
  "Gateway Timeout",
])

export class BackofficeApiError extends Error {
  readonly status: number
  readonly endpoint: string
  readonly method: string
  readonly errorCode?: string
  readonly errorCategory?: string
  readonly traceId?: string
  readonly sessionId?: string
  readonly fields: IErrorField[]
  readonly causes: IErrorCause[]
  readonly userMessage: string
  // The gateway's own text, or undefined when it sent none worth showing.
  // Callers use it to tell a real backend message from one of our fallbacks.
  readonly backendMessage?: string
  readonly cause: AxiosError<IErrorPayload>

  constructor(error: AxiosError<IErrorPayload>) {
    const payload = asPayload(error.response?.data)
    const body = payload.responseBody
    const flatError =
      payload.error && !HTTP_REASON_PHRASES.has(payload.error)
        ? payload.error
        : undefined
    const backendMessage = body?.message ?? payload.message ?? flatError

    super(backendMessage ?? error.message)

    this.name = "BackofficeApiError"
    this.status = error.response?.status ?? payload.status ?? 0
    this.endpoint = error.config?.url ?? ""
    this.method = error.config?.method?.toUpperCase() ?? ""
    this.errorCode = body?.errorCode
    this.errorCategory = body?.errorCategory
    this.traceId = body?.traceId
    this.sessionId = body?.sessionId
    this.fields = body?.fields ?? []
    this.causes = body?.causes ?? []
    const preRequestMessage = error.config ? undefined : error.message
    const statusMessage = STATUS_MESSAGES[this.status]
    // A 403 body carries the gateway's own generic "Forbidden"; the status
    // message names the cause an agent can act on — not being on the VPN.
    const bodyMessage = this.status === 403 ? undefined : backendMessage
    this.backendMessage = bodyMessage
    this.userMessage =
      bodyMessage ?? statusMessage ?? preRequestMessage ?? DEFAULT_ERROR_MESSAGE
    this.cause = error
  }

  get reportContext() {
    return {
      status: this.status,
      endpoint: this.endpoint,
      method: this.method,
      errorCode: this.errorCode,
      errorCategory: this.errorCategory,
      traceId: this.traceId,
      sessionId: this.sessionId,
    }
  }
}

export const isBackofficeApiError = (
  error: unknown,
): error is BackofficeApiError => error instanceof BackofficeApiError
